/**
 * MapLibre adapter — tek giriş noktası.
 * Analiz modülleri map.addLayer / removeLayer ÇAĞIRMAZ; yalnız bu registry üzerinden.
 */
import { markRaw } from 'vue'
import { EMPTY_FC } from './gtfsMapLayers.js'

let _map = null
/** @type {Map<string, { category: string, ownerId: string, layerId: string, descriptor: object }>} */
const _runtime = new Map()

function layerKey(category, ownerId, layerId) {
  return `${category}::${ownerId}::${layerId}`
}

export function attachMap(map) {
  _map = map || null
}

export function getAttachedMap() {
  return _map
}

export function detachMap() {
  _map = null
  _runtime.clear()
}

/**
 * Source + layer(s) kaydı. Idempotent.
 * @param {'base'|'analysis'|'reference'|'interaction'} category
 * @param {string} ownerId
 * @param {string} layerId
 * @param {object} layerInstance
 */
export function registerOnMap(category, ownerId, layerId, layerInstance) {
  if (!_map || !layerInstance) return false
  const key = layerKey(category, ownerId, layerId)
  const sourceId = layerInstance.sourceId || layerId
  const descriptor = markRaw({
    sourceId,
    layerIds: [],
    beforeId: layerInstance.beforeId || null,
  })

  if (layerInstance.source && !_map.getSource(sourceId)) {
    _map.addSource(sourceId, layerInstance.source)
  } else if (!_map.getSource(sourceId) && !layerInstance.source) {
    _map.addSource(sourceId, { type: 'geojson', data: EMPTY_FC })
  }

  const layers = Array.isArray(layerInstance.layers)
    ? layerInstance.layers
    : layerInstance.layer
      ? [layerInstance.layer]
      : []

  for (const layerDef of layers) {
    if (!layerDef?.id) continue
    if (!_map.getLayer(layerDef.id)) {
      const def = { ...layerDef, source: layerDef.source || sourceId }
      if (layerInstance.beforeId && _map.getLayer(layerInstance.beforeId)) {
        _map.addLayer(def, layerInstance.beforeId)
      } else {
        _map.addLayer(def)
      }
    }
    descriptor.layerIds.push(layerDef.id)
  }

  if (!descriptor.layerIds.length && layerInstance.layerIds) {
    descriptor.layerIds = [...layerInstance.layerIds]
  }

  _runtime.set(key, { category, ownerId, layerId, descriptor })
  return true
}

export function removeFromMap(category, ownerId, layerId) {
  if (!_map) {
    _runtime.delete(layerKey(category, ownerId, layerId))
    return
  }
  const key = layerKey(category, ownerId, layerId)
  const entry = _runtime.get(key)
  const layerIds = entry?.descriptor?.layerIds || []
  const sourceId = entry?.descriptor?.sourceId

  for (let i = layerIds.length - 1; i >= 0; i -= 1) {
    const id = layerIds[i]
    if (_map.getLayer(id)) {
      try {
        _map.removeLayer(id)
      } catch {
        /* ignore */
      }
    }
  }

  if (sourceId && _map.getSource(sourceId)) {
    const stillUsed = [..._runtime.values()].some(
      (e) =>
        e.descriptor?.sourceId === sourceId &&
        !(
          e.category === category &&
          e.ownerId === ownerId &&
          e.layerId === layerId
        )
    )
    if (!stillUsed) {
      try {
        _map.removeSource(sourceId)
      } catch {
        /* ignore */
      }
    }
  }

  _runtime.delete(key)
}

export function setVisibilityOnMap(layerIds, visible) {
  if (!_map) return
  const ids = Array.isArray(layerIds) ? layerIds : [layerIds]
  for (const id of ids) {
    if (!_map.getLayer(id)) continue
    _map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none')
  }
}

export function setOpacityOnMap(layerIds, paintProp, opacity) {
  if (!_map) return
  const ids = Array.isArray(layerIds) ? layerIds : [layerIds]
  for (const id of ids) {
    if (!_map.getLayer(id)) continue
    try {
      _map.setPaintProperty(id, paintProp, opacity)
    } catch {
      /* ignore */
    }
  }
}

export function setSourceDataOnMap(sourceId, data) {
  if (!_map) return
  const src = _map.getSource(sourceId)
  if (src && typeof src.setData === 'function') {
    src.setData(data || EMPTY_FC)
  }
}

export function clearSourceDataOnMap(sourceId) {
  setSourceDataOnMap(sourceId, EMPTY_FC)
}

export function getRuntimeEntry(category, ownerId, layerId) {
  return _runtime.get(layerKey(category, ownerId, layerId)) || null
}

export function listRuntimeByCategory(category) {
  return [..._runtime.values()].filter((e) => e.category === category)
}

export function moveLayerOnMap(layerId, beforeId) {
  if (!_map?.getLayer(layerId)) return
  try {
    if (beforeId && _map.getLayer(beforeId)) {
      _map.moveLayer(layerId, beforeId)
    } else {
      _map.moveLayer(layerId)
    }
  } catch {
    /* ignore */
  }
}

export function __resetRegistryForTests() {
  _map = null
  _runtime.clear()
}
